import { amountText, BOTTLE_FIRSTS, type BottleUnit, formatAmount } from "@babia/checks";
import type { BabyMilestones } from "../../lib/queries.ts";
import { clock, dur, monthDay, weekdayDay } from "../../lib/time.ts";

type Milestone = BabyMilestones["firsts"][number];

/** A milestone counts as news for three days. */
const NEWS_MS = 72 * 60 * 60 * 1000;
/**
 * The most the strip shows. A sleep together is a milestone for each pair of
 * babies, so one good night with 10 babies (`MAX_BABIES`) can bring 45.
 */
const MOST_NEWS = 6;

/** "first", "5th", "100th", "1,000th". */
function ordinal(n: number): string {
  if (n === 1) return "first";
  const teen = n % 100 >= 11 && n % 100 <= 13;
  return `${n.toLocaleString("en-US")}${teen ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th")}`;
}

/** What a milestone is, after the baby's name; a bottle's size is in the household's unit. */
const WHAT: Record<
  Exclude<Milestone["kind"], "together">,
  (threshold: number, unit: BottleUnit) => string
> = {
  sleep: (h) => `first ${h}-hour stretch`,
  bottle: (size, unit) => `first ${amountText(size, unit)} bottle`,
  "bottle-gap": (h) => `first ${h} hours between bottles`,
  "bottle-count": (n) => `${ordinal(n)} bottle`,
  "diaper-count": (n) => `${ordinal(n)} diaper change`,
  gallons: (n) => `${ordinal(n)} gallon`,
  litres: (n) => `${ordinal(n)} litre`,
};

/**
 * "Ada's first 6-hour stretch" with a name, "First 6-hour stretch" without.
 * A sleep together names the other baby either way.
 */
export function milestoneTitle(
  m: Milestone,
  unit: BottleUnit,
  name?: string,
  withName = "",
): string {
  if (m.kind === "together")
    return name
      ? `${name} and ${withName}'s first ${m.threshold}-hour sleep together`
      : `First ${m.threshold}-hour sleep with ${withName}`;
  const what = WHAT[m.kind](m.threshold, unit);
  return name ? `${name}'s ${what}` : what[0]!.toUpperCase() + what.slice(1);
}

export function milestoneDetail(m: Milestone, unit: BottleUnit): string {
  if (m.endedAt)
    return `${dur(m.at, m.endedAt)}, ${weekdayDay(m.at)} ${clock(m.at)} – ${clock(m.endedAt)}`;
  const when = `${weekdayDay(m.at)} at ${clock(m.at)}`;
  if (m.kind === "bottle")
    return `${m.amountOz === null ? amountText(m.threshold, unit) : formatAmount(m.amountOz, unit)}, ${when}`;
  if (m.kind === "gallons" || m.kind === "litres") return `Every bottle added up, ${when}`;
  return when;
}

const milestoneKey = (m: Milestone) => `${m.kind}:${m.threshold}:${m.withBabyId ?? ""}`;

/** When a milestone happened: a stretch counts once it ends. */
const reached = (m: Milestone) => (m.endedAt ?? m.at).getTime();

/**
 * Milestones reached in the last three days, newest first, across babies. A
 * sleep together is in both babies' lists; it is news once.
 */
export function recentMilestones(
  all: BabyMilestones[],
  now: Date,
): { babyId: string; milestone: Milestone }[] {
  const seen = new Set<string>();
  return all
    .flatMap((b) => b.firsts.map((milestone) => ({ babyId: b.babyId, milestone })))
    .filter(({ babyId, milestone: m }) => {
      // Never ahead of now: a partner's phone clock can run a little fast.
      const age = now.getTime() - reached(m);
      if (age < 0 || age > NEWS_MS) return false;
      if (m.kind !== "together") return true;
      const pair = `${m.threshold}:${[babyId, m.withBabyId].sort().join(":")}`;
      if (seen.has(pair)) return false;
      seen.add(pair);
      return true;
    })
    .sort((a, b) => reached(b.milestone) - reached(a.milestone));
}

/** The celebratory strip at the top of the overview: the newest `MOST_NEWS`, then how many more. */
export function MilestoneNews({
  items,
  nameOf,
  unit,
}: {
  items: { babyId: string; milestone: Milestone }[];
  nameOf: (babyId: string) => string;
  unit: BottleUnit;
}) {
  if (!items.length) return null;
  return (
    <section className="mt-6" aria-label="New milestones">
      <h2 className="kicker">New milestone{items.length > 1 ? "s" : ""}</h2>
      <ul className="mt-2.5 grid gap-2.5 md:grid-cols-2">
        {items.slice(0, MOST_NEWS).map(({ babyId, milestone: m }) => (
          <li
            key={`${babyId}:${milestoneKey(m)}`}
            className="border-l-[3px] border-accent bg-accent-tint px-4 py-3 text-accent-deep"
          >
            <div className="heading text-[19px]">
              {milestoneTitle(m, unit, nameOf(babyId), nameOf(m.withBabyId ?? ""))}
            </div>
            <p className="mt-0.5 text-[13px]">{milestoneDetail(m, unit)}</p>
          </li>
        ))}
      </ul>
      {items.length > MOST_NEWS ? (
        <p className="mt-2 text-[13px] opacity-65">
          And {items.length - MOST_NEWS} more under Milestones, below.
        </p>
      ) : null}
    </section>
  );
}

/** The example first bottle while there are no firsts yet: the third size that counts (4 oz, 120 ml). */
const exampleBottle = (unit: BottleUnit) => amountText(BOTTLE_FIRSTS[unit][2]!, unit);

/** One baby's records and every milestone so far, newest first, in the household's unit. */
export function MilestoneList({
  data,
  nameOf,
  unit,
  sleep = true,
}: {
  data: BabyMilestones | undefined;
  nameOf: (babyId: string) => string;
  unit: BottleUnit;
  /** Whether the family tracks sleep, for the examples while there are none. */
  sleep?: boolean;
}) {
  if (!data) return <p className="py-3 text-[14px] italic opacity-60">Nothing logged yet.</p>;
  const together = data.longestSleepTogether;
  const records = [
    data.longestSleep && {
      label: "Longest stretch",
      value: dur(data.longestSleep.at, data.longestSleep.endedAt),
      when: monthDay(data.longestSleep.at),
    },
    together && {
      label: `Longest asleep with ${nameOf(together.withBabyId)}`,
      value: dur(together.at, together.endedAt),
      when: monthDay(together.at),
    },
    data.longestBottleGap && {
      label: "Longest between bottles",
      value: dur(data.longestBottleGap.at, data.longestBottleGap.endedAt),
      when: monthDay(data.longestBottleGap.at),
    },
    data.biggestBottle && {
      label: "Biggest bottle",
      value: formatAmount(data.biggestBottle.amountOz, unit),
      when: monthDay(data.biggestBottle.at),
    },
    data.trackingSince && {
      label: "Logging since",
      value: monthDay(data.trackingSince),
      when: null,
    },
  ].filter((r) => !!r);

  return (
    <div>
      <dl>
        {records.map((r) => (
          <div key={r.label} className="flex items-baseline gap-4 border-t border-rule py-2">
            <dt className="text-[13px] opacity-65">{r.label}</dt>
            <dd className="ml-auto text-right">
              <span className="heading text-[17px]">{r.value}</span>
              {r.when ? <span className="ml-2 text-[12px] opacity-60">{r.when}</span> : null}
            </dd>
          </div>
        ))}
      </dl>
      {data.firsts.length ? (
        <ol className="mt-3">
          {data.firsts.map((m) => (
            <li
              key={milestoneKey(m)}
              className="flex items-baseline gap-3 border-t border-rule py-2"
            >
              <span className="w-[52px] shrink-0 text-[12px] tabular-nums opacity-60">
                {monthDay(m.at)}
              </span>
              <span className="min-w-0">
                <span className="heading text-[15px]">
                  {milestoneTitle(m, unit, undefined, nameOf(m.withBabyId ?? ""))}
                </span>
                <span className="block text-[12px] opacity-65">{milestoneDetail(m, unit)}</span>
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-3 border-t border-rule pt-2 text-[13px] italic opacity-60">
          {sleep
            ? `Firsts appear here — the first 3-hour stretch, the first ${exampleBottle(unit)} bottle.`
            : `Firsts appear here — the first ${exampleBottle(unit)} bottle, the 100th bottle.`}
        </p>
      )}
    </div>
  );
}
